<script lang="ts" setup generic="T extends ProductSearchGolf | GolfLite">
  import type { LatLngTuple, Marker } from 'leaflet'

  import type { ProductSearchGolf } from '~/core/types/products'
  import type { GolfLite } from '~/core/types/shared'

  import GolfCardWrapper from './GolfCardWrapper.vue'

  type Props = {
    golf: T
    readonly?: boolean
    displayPrice?: boolean
    zoom: number
    highlightedGolfMarkers: number[]
  }
  const props = defineProps<Props>()

  const emit = defineEmits<{
    (e: 'click', golf: T): void
  }>()

  const isSelected = ref(false)

  const latLng = computed((): LatLngTuple => {
    const { position } = props.golf
    return [Number(position.latitude) || 0, Number(position.longitude) || 0]
  })

  const dynamicSize = computed(() => {
    const displayPrice = props.zoom >= 8 && props.displayPrice && 'best_price' in props.golf
    const circleSize = [props.zoom * 2, props.zoom * 2]
    return displayPrice ? [72, 36] : circleSize
  })

  const dynamicAnchor = computed(() => {
    const displayPrice = props.zoom >= 8 && props.displayPrice && 'best_price' in props.golf
    const circleSize = [props.zoom, props.zoom]
    return displayPrice ? [36, 18] : circleSize
  })

  const isHighlighted = computed(() => props.highlightedGolfMarkers.includes(props.golf.id))

  function handleOutMarker() {
    isSelected.value = false
  }

  function handleOverMarker() {
    isSelected.value = true
    openPopup()
  }

  const markerRef = useTemplateRef('markerRef')

  function openPopup() {
    if (!markerRef.value) {
      return
    }
    const marker = markerRef.value.leafletObject as Marker
    marker.openPopup()
  }

  function handleClickMarker() {
    emit('click', props.golf)
  }
</script>

<template>
  <LMarker
    is="span"
    ref="markerRef"
    :lat-lng="latLng"
    @mouseover="handleOverMarker"
    @mouseout="handleOutMarker"
    @click="handleClickMarker"
  >
    <LIcon
      class-name=""
      :icon-size="dynamicSize"
      :icon-anchor="dynamicAnchor"
    >
      <span
        v-if="'best_price' in props.golf"
        :class="`${zoom >= 8 ? 'price': 'circle'} ${isHighlighted ? 'is-highlighted' : ''}`"
      >
        {{ props.golf.best_price }}€
      </span>
      <span
        v-else
        :class="`circle ${isHighlighted ? 'is-highlighted' : ''}`"
      />
    </LIcon>
    <LPopup v-if="!readonly">
      <Paper>
        <GolfCardWrapper
          :golf="golf"
          is-home
          rating
          departures
        />
      </Paper>
    </LPopup>
  </LMarker>
</template>
<style scoped>

.price {
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 0.25rem;
  border-radius: 36px;
  background-color: var(--color-secondary);
  color: white;
  font-family: Bariol, sans-serif;
  font-size: 1.5rem;
  font-weight: bold;

  &.is-highlighted {
    background-color: var(--color-primary);
  }
}

.circle {
  display: flex;
  width: 100%;
  height: 100%;
  border-radius: 100%;
  background-color: var(--color-secondary);
  font-size: 0;

  &.is-highlighted {
    background-color: var(--color-primary);
  }
}
</style>
